import React, { useState, useRef } from "react"
import { Switch } from "@headlessui/react"
import ErrorBanner from "./error-banner"
import config from "../lib/config"

function classNames(...classes) {
  return classes.filter(Boolean).join(" ")
}

export default function Example() {
  const [agreed, setAgreed] = useState(false)
  const [completedForm, setCompletedForm] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const yourName = useRef(null)
  const yourEmail = useRef(null)
  const yourSchool = useRef(null)
  const yourMessage = useRef(null)

  const isset = x => x && x.length > 0
  const submitForm = () => {
    if (!agreed) {
      return setErrorMessage("Please agree to allow us to contact you.")
    }
    const res = {
      name: yourName.current.value,
      email: yourEmail.current.value,
      school: yourSchool.current.value,
      message: yourMessage.current.value,
    }
    if (isset(res.name) && isset(res.email) && isset(res.school)) {
      // everything is filled in
      fetch("https://private.xtrp.io/projects/php-request-emailer/", {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({
          form: `${config.siteTitle} Get In Touch`,
          ...res,
        }),
      })
        .then(function () {
          setCompletedForm(true)
        })
        .catch(function () {
          setErrorMessage(
            `A server error occurred. Get in touch with ${config.contactEmail} directly instead.`
          )
        })
    } else {
      return setErrorMessage("Please complete all the required fields.")
    }
  }

  return (
    <>
      {errorMessage.length > 0 ? (
        <ErrorBanner text={errorMessage} close={() => setErrorMessage("")} />
      ) : null}
      <div
        className="bg-white py-16 px-4 overflow-hidden sm:px-6 lg:px-8 lg:py-24"
        id="interested"
      >
        <div className="relative max-w-xl mx-auto">
          <svg
            className="absolute left-full transform translate-x-1/2"
            width={404}
            height={404}
            fill="none"
            viewBox="0 0 404 404"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="85737c0e-0916-41d7-917f-596dc7edfa27"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={404}
              fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
            />
          </svg>
          <svg
            className="absolute right-full bottom-0 transform -translate-x-1/2"
            width={404}
            height={404}
            fill="none"
            viewBox="0 0 404 404"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="85737c0e-0916-41d7-917f-596dc7edfa27"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={404}
              fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
            />
          </svg>
          {completedForm ? (
            <div>
              <h2 className="text-center text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                We'll Be In Touch Soon!
              </h2>
              <p className="text-center mt-4 text-lg leading-7 text-gray-500">
                We'll send you an email in the next couple of days with more
                information as well as an opportunity to meet the team and get a
                full demo.
              </p>
              <p className="text-center mt-4 text-lg leading-7 text-gray-500">
                We're looking forward to working with you.
              </p>
            </div>
          ) : (
            <>
              <div>
                <h2 className="text-center text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                  Get More Information <br /> & Schedule A Demo
                </h2>
                <p className="text-center mt-4 text-lg leading-7 text-gray-500">
                  Complete this quick form to get a full live demo, learn more
                  about the product, and meet with the team.
                </p>
                <p className="text-center mt-6 py-3 border rounded-md text-md leading-6 text-gray-500">
                  Want to get in touch via email instead? Contact{" "}
                  <a
                    href={`mailto:${config.contactEmail}`}
                    className="styled-link"
                  >
                    {config.contactEmail}
                  </a>
                  .
                </p>
              </div>
              <div className="mt-12">
                <form
                  onSubmit={evt => {
                    evt.preventDefault()
                    submitForm()
                    return false
                  }}
                  className="grid grid-cols-1 gap-y-6"
                >
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Your Name
                    </label>
                    <div className="mt-1">
                      <input
                        ref={yourName}
                        type="text"
                        name="name"
                        id="name"
                        autoComplete="given-name"
                        className="py-3 px-4 block w-full shadow-sm focus:ring-default-500 focus:border-default-500 border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="school"
                      className="block text-sm font-medium text-gray-700"
                    >
                      The School You Represent
                    </label>
                    <div className="mt-1">
                      <input
                        ref={yourSchool}
                        type="text"
                        name="school"
                        id="school"
                        autoComplete="organization"
                        className="py-3 px-4 block w-full shadow-sm focus:ring-default-500 focus:border-default-500 border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Your Email Address
                    </label>
                    <div className="mt-1">
                      <input
                        ref={yourEmail}
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        className="py-3 px-4 block w-full shadow-sm focus:ring-default-500 focus:border-default-500 border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Message (optional)
                    </label>
                    <div className="mt-1">
                      <textarea
                        ref={yourMessage}
                        id="message"
                        name="message"
                        rows={4}
                        className="py-3 px-4 block w-full shadow-sm focus:ring-default-500 focus:border-default-500 border-gray-300 rounded-md"
                        defaultValue={""}
                      />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <Switch
                          checked={agreed}
                          onChange={setAgreed}
                          className={classNames(
                            agreed ? "bg-default-600" : "bg-gray-200",
                            "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-default-500"
                          )}
                        >
                          <span className="sr-only">Agree to policies</span>
                          <span
                            aria-hidden="true"
                            className={classNames(
                              agreed ? "translate-x-5" : "translate-x-0",
                              "inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                            )}
                          />
                        </Switch>
                      </div>
                      <div className="ml-3">
                        <p className="text-base text-gray-500">
                          By selecting this, you allow us to get in touch via
                          your email.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      className="transition-all w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-default-600 hover:bg-default-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-default-500"
                    >
                      Let's Talk
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
